import {Banner, SearchField, Table} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import React, {useState} from 'react';

import {FILE_EXPORT_DOC_URL, MAX_NUM_FILES, PAGE_SIZE} from '../constants';
import {FileBrowserLayout} from '../FileBrowserLayout';
import {FilePath} from '../FilePath';
import type {
  FileBrowserFile,
  FolderData,
  RunFileScope,
  UseFileBrowserLoadFile,
} from '../types';
import {FileRow} from './FileRow';
import {FolderRow} from './FolderRow';
import {FolderViewWrapper} from './FolderViewWrapper';

interface FolderViewProps {
  filePath: string[];
  folder: FolderData;
  onDownload(file: FileBrowserFile): void;
  onSetFilePath(path: string[]): void;
  totalFiles: number;
  useLoadFile: UseFileBrowserLoadFile;
  runFileScope?: RunFileScope;
}

export function FolderView({
  filePath,
  folder,
  onDownload,
  runFileScope,
  onSetFilePath,
  totalFiles,
  useLoadFile,
}: FolderViewProps) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');

  // separate subfolders from files in this directory
  const rootFiles = folder.files;
  const subfolderKeys = Object.keys(folder.subdirectories).sort();
  const needsPagination = subfolderKeys.length + rootFiles.length > PAGE_SIZE;

  const foldersAndFiles = [...subfolderKeys, ...rootFiles].filter(
    folderOrFile => {
      const match = search.toLowerCase().trim();
      if (typeof folderOrFile === 'string') {
        return folderOrFile.toLowerCase().indexOf(match) !== -1;
      }

      return folderOrFile.name.toLowerCase().indexOf(match) !== -1;
    }
  );

  const handleSearchChange = (search: string) => {
    setPage(0);
    setSearch(search);
  };

  const pagination = needsPagination
    ? {
        hasPreviousPage: page > 0,
        hasNextPage: (page + 1) * PAGE_SIZE < foldersAndFiles.length,
        onPrevious: () => setPage(p => Math.max(0, p - 1)),
        onNext: () => setPage(p => p + 1),
        range: {
          pageIndex: page,
          pageSize: PAGE_SIZE,
          totalCount: foldersAndFiles.length,
        },
      }
    : undefined;

  const toolbar = () => {
    return (
      <SearchField
        fullWidth
        placeholder="Search"
        aria-label="Search"
        value={search}
        onChange={handleSearchChange}
      />
    );
  };

  return (
    <FileBrowserLayout topLeft={<FilePath filePath={filePath} />}>
      {totalFiles > MAX_NUM_FILES ? (
        <Banner variant="warning" isDismissible={false}>
          File view is truncated to the first {MAX_NUM_FILES} files. To view the
          rest, use our{' '}
          <TargetBlank href={FILE_EXPORT_DOC_URL}>
            Python file export API.
          </TargetBlank>
        </Banner>
      ) : null}
      <FolderViewWrapper>
        <Table
          aria-label="File browser table"
          toolbar={toolbar}
          pagination={pagination}>
          <Table.Header>
            <Table.Column isRowHeader>
              <div className="flex pl-32">File name</div>
            </Table.Column>
            <Table.Column>Last modified</Table.Column>
            <Table.Column>Size</Table.Column>
            <Table.Column>
              <div className="flex w-full justify-end pr-8">
                {runFileScope ? 'Actions' : 'Download'}
              </div>
            </Table.Column>
          </Table.Header>
          <Table.Body>
            {foldersAndFiles
              .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
              .map(folderOrFile => {
                if (typeof folderOrFile === 'string') {
                  return (
                    <FolderRow
                      key={'folder-' + folderOrFile}
                      folder={folder.subdirectories[folderOrFile]}
                      folderName={folderOrFile}
                      path={filePath}
                      onSetFilePath={onSetFilePath}
                    />
                  );
                } else {
                  return (
                    <FileRow
                      key={'file-' + folderOrFile.name}
                      file={folderOrFile}
                      path={filePath}
                      onSetFilePath={onSetFilePath}
                      runFileScope={runFileScope}
                      onDownload={onDownload}
                      useLoadFile={useLoadFile}
                    />
                  );
                }
              })}
          </Table.Body>
        </Table>
      </FolderViewWrapper>
    </FileBrowserLayout>
  );
}
